"use client";

import { useCallback, useEffect, useRef, useState } from "react";

export interface UseInViewOptions {
  /**
   * Distance from the viewport at which the element is considered "in view".
   * A positive margin pre-triggers before the element is actually visible.
   */
  rootMargin?: string;
  /**
   * Intersection ratio (0–1) or list of thresholds passed to `IntersectionObserver`.
   * Default `0` fires as soon as any pixel intersects the root.
   */
  threshold?: number | number[];
  /** When true (default), stop observing after the first time the element enters view. */
  once?: boolean;
}

export interface UseInViewResult<T extends Element> {
  /** Callback or object ref — prefer attaching directly: `ref={ref}`. */
  ref: React.Ref<T>;
  inView: boolean;
}

function applyIntersectionEntries(
  entries: IntersectionObserverEntry[],
  once: boolean,
  setInView: (value: boolean) => void,
  disconnect: () => void
): boolean {
  for (const entry of entries) {
    if (entry.isIntersecting) {
      setInView(true);
      if (once) {
        disconnect();
        return true;
      }
    } else if (!once) {
      setInView(false);
    }
  }
  return false;
}

/**
 * Tracks whether an element is in (or near) the viewport using `IntersectionObserver`.
 *
 * Unlike native `loading="lazy"`, `IntersectionObserver` correctly reports visibility for elements
 * moved by CSS `transform` (e.g. horizontal carousels that translate their track). Native lazy
 * loading can strand such images — they finish layout off-screen, never receive a page scroll event,
 * and occasionally never load when translated into view. This hook is the transform-aware
 * replacement used to gate image loading.
 *
 * SSR/first-client render is deterministic (`inView` starts `false`) to avoid hydration mismatches;
 * the observer then updates it on the client. Environments without `IntersectionObserver`
 * (e.g. jsdom, legacy browsers) fall back to `inView = true` so content still loads.
 *
 * Uses a callback ref so observation starts as soon as the node mounts (avoids a missed
 * attach when `ref.current` was still null in an effect-only setup).
 */
export function useInView<T extends Element>({
  rootMargin = "400px",
  threshold = 0,
  once = true,
}: UseInViewOptions = {}): UseInViewResult<T> {
  const [element, setElement] = useState<T | null>(null);
  const [inView, setInView] = useState(false);
  const onceRef = useRef(once);
  onceRef.current = once;

  const ref = useCallback((node: T | null) => {
    setElement(node);
  }, []);

  useEffect(() => {
    if (!element) return;

    // Fallback for environments without IntersectionObserver: load immediately.
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        applyIntersectionEntries(entries, onceRef.current, setInView, () => observer.disconnect());
      },
      { rootMargin, threshold }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [element, rootMargin, threshold]);

  return { ref, inView };
}
